import type { Celda } from './tipos';

/**
 * Tiempo y costo de cada celda de una corrida
 * (ADR-2026-10-13-tiempo-y-costo-en-el-test-masivo). Puro, sin supabase.
 *
 * - **Costo**: `datos_generacion.cost_usd` de cada fila del execution_id, sumado. Es el
 *   gasto de IA de esa imagen con los intentos fallidos de la cadena incluidos (el mismo
 *   número que `executions.cost_usd`, que desde el front no se puede leer: RLS cerrada).
 *   Es un piso: un modelo sin tarifa verificada se registra sin costo.
 * - **Tiempo**: desde el disparo de la celda (`disparada_en`, reloj del navegador) hasta
 *   que llegó la ÚLTIMA imagen (`created_at` de la fila, reloj de la base). Es lo que
 *   esperaría un usuario: incluye la cola del backend, no sólo la generación.
 */

export interface FilaConMetricas {
  execution_id: string;
  file_name: string | null;
  created_at?: string | null;
  /** Número del JSON, o texto si se guardó así; cualquier otra cosa = sin dato. */
  cost_usd?: unknown;
}

export interface MetricasDeCelda {
  /** null = sin hora de disparo o sin imagen entregada. */
  tiempoMs: number | null;
  /** null = ninguna fila trajo costo. */
  costoUsd: number | null;
}

interface PorExecution {
  costoUsd: number | null;
  ultimaEn: number | null;
}

export function agruparPorExecution(filas: FilaConMetricas[]): Map<string, PorExecution> {
  const mapa = new Map<string, PorExecution>();
  for (const f of filas) {
    const acc = mapa.get(f.execution_id) ?? { costoUsd: null, ultimaEn: null };
    // `->` de PostgREST devuelve el número del JSON, pero un valor guardado como texto
    // llega como string: se aceptan los dos.
    const costo =
      typeof f.cost_usd === 'number' ? f.cost_usd
      : typeof f.cost_usd === 'string' && f.cost_usd.trim() !== '' ? Number(f.cost_usd)
      : NaN;
    if (Number.isFinite(costo)) acc.costoUsd = (acc.costoUsd ?? 0) + costo;
    // Sólo las filas con imagen marcan el final: una fila de error no es una entrega.
    const en = f.file_name && f.created_at ? Date.parse(f.created_at) : NaN;
    if (Number.isFinite(en)) acc.ultimaEn = Math.max(acc.ultimaEn ?? 0, en);
    mapa.set(f.execution_id, acc);
  }
  return mapa;
}

export function metricasDeCelda(celda: Celda, porExecution: Map<string, PorExecution>): MetricasDeCelda {
  const m = porExecution.get(celda.execution_id);
  const desde = celda.disparada_en;
  const tiempo = m?.ultimaEn != null && desde != null ? m.ultimaEn - desde : null;
  return {
    // Negativo sólo con el reloj del navegador adelantado: mejor no mostrar nada.
    tiempoMs: tiempo != null && tiempo > 0 ? tiempo : null,
    costoUsd: m?.costoUsd ?? null,
  };
}

/** Totales de una corrida: costo sumado y tiempo promedio de las celdas que lo tienen. */
export function resumenDeCorrida(
  celdas: Celda[],
  porExecution: Map<string, PorExecution>,
): { costoUsd: number | null; tiempoPromedioMs: number | null; conTiempo: number } {
  let costo: number | null = null;
  let tiempoTotal = 0;
  let conTiempo = 0;
  for (const c of celdas) {
    const m = metricasDeCelda(c, porExecution);
    if (m.costoUsd != null) costo = (costo ?? 0) + m.costoUsd;
    if (m.tiempoMs != null) {
      tiempoTotal += m.tiempoMs;
      conTiempo += 1;
    }
  }
  return { costoUsd: costo, tiempoPromedioMs: conTiempo ? tiempoTotal / conTiempo : null, conTiempo };
}

export function formatearTiempo(ms: number | null): string {
  if (ms == null) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${String(s)} s`;
  return `${String(Math.floor(s / 60))} min ${String(s % 60).padStart(2, '0')} s`;
}

export function formatearCosto(usd: number | null): string {
  // null = no hay dato (imagen vieja, modelo sin tarifa), NO que haya sido gratis: un
  // costo real de cero se muestra como US$ 0.000.
  if (usd == null) return 'costo s/d';
  return `US$ ${usd.toFixed(usd < 1 ? 3 : 2)}`;
}
